import RJSFForm, { type FormProps as RJSFFormProps, type IChangeEvent } from '@rjsf/core';
import defaultValidator, { customizeValidator, type CustomValidatorOptionsType } from '@rjsf/validator-ajv8';
import type { RJSFSchema } from '@rjsf/utils';
import { forwardRef, useImperativeHandle, useMemo, useRef, type FormEvent } from 'react';
import { useFieldChangeBatcher } from './hooks/useFieldChangeBatcher';

export type FormProps = Omit<RJSFFormProps, 'validator'> & {
  /** Defaults to AJV8 (customized when `additionalMetaSchemas` / `customFormats` are given). */
  validator?: RJSFFormProps['validator'];
  additionalMetaSchemas?: CustomValidatorOptionsType['additionalMetaSchemas'];
  customFormats?: CustomValidatorOptionsType['customFormats'];
  /** v4 compat alias of `customValidate`. */
  validate?: RJSFFormProps['customValidate'];
};

/**
 * Wrapper around @rjsf/core Form that:
 * - Provides a default AJV8 validator (so callers don't need to pass one)
 * - Supports `additionalMetaSchemas` / `customFormats` props via customizeValidator
 * - Accepts the legacy `validate` prop as an alias for `customValidate` (v5+ name)
 * - Cleans form data before calling onSubmit (trims trailing array undefineds,
 *   removes object properties absent from the schema)
 * - Exposes `formContext.enqueueFieldChange(path, value)` to coalesce programmatic field writes
 *   (see useFieldChangeBatcher)
 */
const Form = forwardRef<RJSFForm, FormProps>(function Form(
  {
    onSubmit,
    schema,
    validator: propValidator,
    additionalMetaSchemas,
    customFormats,
    validate,
    customValidate,
    formContext: propFormContext,
    ...props
  },
  ref
) {
  const rjsfRef = useRef<RJSFForm>(null);
  useImperativeHandle(ref, () => rjsfRef.current as RJSFForm);

  const enqueueFieldChange = useFieldChangeBatcher(rjsfRef);
  const formContext = useMemo(() => ({ ...propFormContext, enqueueFieldChange }), [propFormContext, enqueueFieldChange]);

  const validator = useMemo(() => {
    if (propValidator) return propValidator;
    if (additionalMetaSchemas || customFormats) {
      return customizeValidator({ additionalMetaSchemas, customFormats });
    }
    return defaultValidator;
  }, [propValidator, additionalMetaSchemas, customFormats]);

  function handleSubmit({ formData, ...rest }: IChangeEvent, event: FormEvent<any>) {
    const cleaned = cleanFormData(schema, formData);
    if (onSubmit) {
      onSubmit({ formData: cleaned, ...rest }, event);
    }
  }

  return (
    <RJSFForm
      ref={rjsfRef}
      schema={schema}
      validator={validator}
      customValidate={customValidate ?? validate}
      onSubmit={handleSubmit}
      formContext={formContext}
      {...props}
    />
  );
});

export default Form;

/** The subset of JSON Schema that cleanFormData walks. */
type SchemaNode = {
  type?: RJSFSchema['type'];
  items?: SchemaNode;
  properties?: Record<string, SchemaNode>;
  additionalProperties?: SchemaNode | boolean;
};

type DataContainer = Record<string | number, unknown>;

type CleanStackEntry = [schema: SchemaNode, parent: DataContainer, key: string | number, data: unknown];

function cleanFormData(rootSchema: RJSFSchema, formData: unknown): unknown {
  const result: DataContainer = {};
  const stack: CleanStackEntry[] = [[rootSchema as SchemaNode, result, 'root', formData]];

  while (stack.length) {
    const [curSchema, curParent, curName, curData] = stack.shift()!;
    let cleaned = curData;
    const { type, items, properties, additionalProperties } = curSchema;

    switch (type) {
      case 'array':
        if (Array.isArray(curData)) {
          const cleanedArray: unknown[] = [];
          let definedLength = 0;
          curData.forEach((dataItem, index) => {
            if (dataItem !== undefined) definedLength = index + 1;
          });
          curData.slice(0, definedLength).forEach((dataItem, index) => {
            stack.unshift([items!, cleanedArray as unknown as DataContainer, index, dataItem]);
          });
          cleaned = cleanedArray;
        }

        break;
      case 'object':
        if (typeof curData === 'object' && curData) {
          const cleanedObject: DataContainer = {};
          Object.entries(curData).forEach(([key, value]) => {
            if (key in properties!) {
              stack.unshift([properties![key], cleanedObject, key, value]);
            } else if (additionalProperties) {
              stack.unshift([additionalProperties as SchemaNode, cleanedObject, key, value]);
            }
          });
          cleaned = cleanedObject;
        }

        break;
    }

    curParent[curName] = cleaned;
  }

  return result.root;
}
